<template>
  <div class="tabs-component">
    <ul role="tablist" class="tabs-component-tabs" :class="[options.tabClass]">
      <li
        v-for="(tab, i) in tabs"
        :key="'tab-' + i"
        :class="[tab.isActive ? 'is-active' : '', tab.isDisabled ? 'is-disabled' : '', options.tabClass]"
        class="tabs-component-tab"
        role="presentation"
        @click="selectTab(tab.hash, $event)"
        v-show="tab.isVisible"
      >
        <span v-if="tab.counter" class="suffix unread-badge">{{ tab.counter }}</span>
        <a
          :aria-controls="tab.hash"
          :aria-selected="tab.isActive"
          href="javascript:void(0);"
          class="tabs-component-tab-a"
          role="tab"
        >
          <span class="tab-name">{{ tab.name }}</span>
          <span v-if="tab.total" class="tab-total">({{ tab.total }})</span></a
        >
      </li>
      <slot name="actions"></slot>
    </ul>
    <div class="tabs-component-panels">
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * This component contains Tabs templates
 */

export default {
  name: "Tabs",
  props: {
    options: {
      type: Object,
      required: false,
      default: () => ({
        defaultTabHash: null,
        contentClass: "",
        tabClass: ""
      })
    }
  },

  data: () => ({
    tabs: [],
    activeTabHash: "",
    activeTabIndex: 0,
    lastActiveTabHash: ""
  }),

  computed: {
    defaultTabHash() {
      return this.options.defaultTabHash;
    }
  },

  created() {
    this.tabs = this.$children;
  },

  mounted() {
    this.$root.$on("activateTab", tabId => this.selectTab(tabId));

    if (this.options.defaultTabHash !== null && this.findTab("#" + this.options.defaultTabHash)) {
      this.selectTab("#" + this.options.defaultTabHash);
      return;
    }

    if (this.tabs.length) {
      this.selectTab(this.tabs[0].hash);
    }
  },

  watch: {
    defaultTabHash: function(val) {
      if (val) this.selectTab("#" + val);
    }
  },

  methods: {
    findTab(hash) {
      return this.tabs.find(tab => tab.hash === hash);
    },
    selectTab(selectedTabHash, event) {
      const selectedTab = this.findTab(selectedTabHash);

      if (!selectedTab) {
        return;
      }

      if (selectedTab.isDisabled) {
        event.preventDefault();
        return;
      }

      if (this.lastActiveTabHash === selectedTab.hash) {
        this.$root.$emit("clicked", { tab: selectedTab, hash: selectedTab.hash });
        return;
      }

      this.tabs.forEach(tab => {
        tab.isActive = tab.hash === selectedTab.hash;
      });

      this.$emit("changed", { tab: selectedTab, hash: selectedTabHash });
      this.$root.$emit("changedTab", selectedTabHash);

      this.activeTabHash = selectedTab.hash;
      this.activeTabIndex = this.getTabIndex(selectedTabHash);

      this.lastActiveTabHash = this.activeTabHash = selectedTab.hash;
    },

    setTabVisible(hash, visible) {
      const tab = this.findTab(hash);

      if (!tab) {
        return;
      }

      tab.isVisible = visible;

      if (tab.isActive) {
        // If tab is active, set a different one as active.
        tab.isActive = visible;

        this.tabs.every(tab => {
          if (tab.isVisible) {
            tab.isActive = true;

            return false;
          }

          return true;
        });
      }
    },

    getTabIndex(hash) {
      const tab = this.findTab(hash);

      return this.tabs.indexOf(tab);
    },

    getTabHash(index) {
      const tab = this.tabs.find(tab => this.tabs.indexOf(tab) === index);

      if (!tab) {
        return;
      }

      return tab.hash;
    },

    getActiveTab() {
      return this.findTab(this.activeTabHash);
    },

    getActiveTabIndex() {
      return this.getTabIndex(this.activeTabHash);
    }
  }
};
</script>
